import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ReactComponent as HardwareIcClose24px } from '../../../assets/icons/navigation/ic_close_24px.svg';
import { FormFieldContext } from '../form-field/form-field.context';

let nextUniqueId;
nextUniqueId = 0;

const defaultIcons = {
  clear: <HardwareIcClose24px />
};

/**
 * @typedef Props
 * @prop {boolean} [disabled]
 * @prop {string} [className]
 * @prop {{clear: React.ReactNode}} [icons]
 * @prop {string} [id]
 * @prop {boolean} [required]
 * @prop {() => void} [onBlur]
 * @prop {() => void} [onChange]
 * @prop {() => void} [onFocus]
 * @prop {string} [placeholder]
 * @prop {'date' | 'email' | 'number' | 'text' | 'password' | 'tel'} [type]
 * @prop {string | number} [value]
 * @prop {boolean} [hideClearButton]
 * @prop {string} [clearButtonTitle]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifInput = (props) => {
  const {
    className = '',
    disabled = false,
    icons: userIcons = {},
    id = '',
    required = false,
    onBlur = () => null,
    onChange = () => null,
    onFocus = () => null,
    placeholder = null,
    type = 'text',
    value = '',
    hideClearButton = false,
    clearButtonTitle = 'Clear',
    ...other
  } = props;
  const formFieldContext = useContext(FormFieldContext);
  const inputValue = value.toString();
  const inputRef = useRef(null);

  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  useEffect(() => {
    formFieldContext.setRequired(required);
    formFieldContext.setDisabled(disabled);
    formFieldContext.setFormElementType('textInput');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-input-${nextUniqueId++}`);
  }, []);

  useEffect(() => {
    formFieldContext.setValue(inputValue);
    formFieldContext.setHasContent(inputValue.length > 0);
  }, [value]);

  const handleBlur = event => {
    formFieldContext.toggleFocus(event);
    onBlur(event);
  };

  const handleChange = event => {
    formFieldContext.setValue(event.target.value);
    onChange(event);
  };

  const handleFocus = event => {
    formFieldContext.toggleFocus(event);
    onFocus(event);
  };

  const handleClear = () => {
    handleChange({ target: { value: '' } });
    inputRef.current.focus();
  };

  const classes = classNames({
    'motif-input': true,
    'motif-input-invalid': formFieldContext.invalid,
    [className]: true
  });

  return (
    <div className="motif-input-component" data-motif-input>
      <input
        {...other}
        id={formFieldContext.id}
        ref={inputRef}
        disabled={disabled}
        className={classes}
        type={type}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        value={inputValue}
        required={required}
        placeholder={placeholder}
      />

      {inputValue.length > 0 && !hideClearButton && (
        <button
          className="motif-input-clear-button"
          data-testid="clear-button"
          onClick={handleClear}
          disabled={disabled}
          title={clearButtonTitle}
        >
          {icons.clear}
        </button>
      )}
    </div>
  );
};

MotifInput.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  icons: PropTypes.shape({
    clear: PropTypes.node
  }),
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  hideClearButton: PropTypes.bool,
  clearButtonTitle: PropTypes.string,
  type: PropTypes.oneOf(['date', 'email', 'number', 'text', 'password', 'tel']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export { MotifInput };
